import { useState } from 'react'
import { Button, StatusMessage } from '@krp/ui-kit'
import type { CashierCandidate } from '../../../shared/candidates'
import type { SourceItem, SourcesLoad, SourceKinds } from '../../../shared/sourceOptions'
import { emptyProfileInput, profileInputOf, type OwnCashierProfileInput } from '../model/profile'
import type { OwnCashier, OwnCashierLink } from '../model/types'
import { OwnCashierCodeCard } from './OwnCashierCodeCard'
import { OwnCashierLinksCard } from './OwnCashierLinksCard'
import { OwnCashierProfileForm } from './OwnCashierProfileForm'
import './ownCashiers.css'

export interface OwnCashierCardProps {
  /** Создаём нового кассира (id «new») или правим существующего. */
  creating: boolean
  /** Правимый кассир; при создании — `undefined`. */
  cashier: OwnCashier | undefined
  codeMinLength: number
  codeMaxLength: number
  canManage: boolean
  busy: boolean
  /** Текст ошибки сервера дословно; `null` — ошибки нет. */
  error: string | null
  /** Что только что сделано — подтверждение словами; `null` — ничего. */
  done: string | null
  /** Карточка открыта, но кассир по этому id не найден (удалён или ещё не прочитан список). */
  missing: boolean
  create: (input: OwnCashierProfileInput, code: string) => Promise<boolean>
  saveProfile: (cashier: OwnCashier, input: OwnCashierProfileInput, archived: boolean) => Promise<boolean>
  setCode: (cashier: OwnCashier, code: string) => Promise<boolean>
  /** Источники, в которых человек управляет кассирами: в них ищут кассира для связи. */
  linkSources: SourceItem[]
  kinds?: SourceKinds
  sourcesLoad?: SourcesLoad
  linkResourceId: string
  linkSearch: string
  candidates: CashierCandidate[] | undefined
  candidatesLoading: boolean
  chooseLinkSource: (resourceId: string) => void
  findLink: (value: string) => void
  saveLinks: (cashier: OwnCashier, links: OwnCashierLink[]) => Promise<boolean>
  /** Открыть настройки кассира в его источнике (оборудование, касса и вид цены по умолчанию). */
  openSourceSettings: (link: OwnCashierLink) => void
  /** Закрыть карточку (крестик окна даёт то же). */
  close: () => void
}

/**
 * Карточка кассира нашей системы (RP-CU3) внутри модального окна: ФИО и должность, код и где кассир есть в источниках. Оборудование и значения РМК
 * по умолчанию — у кассира в каждом источнике (RP-CU10): здесь только ссылка на них.
 * Данные и команды приходят пропсами; здесь только ввод человека. Создание и правка — одна карточка, вид выбирает `creating`.
 */
export function OwnCashierCard(props: OwnCashierCardProps) {
  const [archiving, setArchiving] = useState(false)
  const selected = props.cashier
  return <section className="own-cashier-card" aria-label={props.creating ? 'Новый кассир' : 'Карточка кассира'}>
    {props.error !== null && <StatusMessage kind="error" title="Не удалось выполнить действие" text={props.error} />}
    {props.done !== null && <p role="status">✔ {props.done}</p>}
    {props.creating ? <section className="own-cashier-card__block" aria-label="Новый кассир">
      <OwnCashierProfileForm initial={emptyProfileInput} withCode codeMinLength={props.codeMinLength} codeMaxLength={props.codeMaxLength} canManage={props.canManage} busy={props.busy}
        submitLabel="Создать кассира" submitActionId="receipts.own-cashiers.create" submit={(input, code) => props.create(input, code ?? '')} cancel={props.close} />
    </section> : selected === undefined ? <p role="status">{props.missing ? 'Кассир не найден: возможно, он удалён. Закройте окно и обновите список.' : 'Загружаем кассира…'}</p> : <>
      <section className="own-cashier-card__block">
        {selected.lastName === null && <p role="status">⚠ ФИО не заполнено: кассир заведён прежним окном связей. Укажите фамилию и имя.</p>}
        <OwnCashierProfileForm key={`${selected.id}:${selected.version}`} initial={profileInputOf(selected)} withCode={false} codeMinLength={props.codeMinLength} codeMaxLength={props.codeMaxLength}
          canManage={props.canManage && selected.canEdit} busy={props.busy} submitLabel="Сохранить карточку" submitActionId="receipts.own-cashiers.save-profile"
          submit={(input) => props.saveProfile(selected, input, selected.archived)}
          headerRight={props.canManage && selected.canEdit
            ? (selected.archived
              ? <Button actionId="receipts.own-cashiers.restore" disabled={props.busy || selected.lastName === null}
                disabledReason={selected.lastName === null ? 'Сначала заполните фамилию и имя' : null}
                onClick={() => { void props.saveProfile(selected, profileInputOf(selected), false) }}>Вернуть из архива</Button>
              : <Button variant="danger" actionId="receipts.own-cashiers.archive" disabled={props.busy || selected.lastName === null}
                disabledReason={selected.lastName === null ? 'Сначала заполните фамилию и имя' : null} onClick={() => setArchiving(true)}>Пометить на удаление (в архив)</Button>)
            : undefined} />
        {archiving && !selected.archived && <div role="group" aria-label="Подтверждение архива">
          <p>Кассир уйдёт в архив: его связи с источниками и код будут сняты, в РМК он войти не сможет. Продолжить?</p>
          <Button variant="danger" actionId="receipts.own-cashiers.archive" busy={props.busy}
            onClick={() => { setArchiving(false); void props.saveProfile(selected, profileInputOf(selected), true) }}>Убрать в архив</Button>
          <Button actionId="receipts.own-cashiers.choose" onClick={() => setArchiving(false)}>Отмена</Button>
        </div>}
      </section>
      {!selected.archived && <>
        <OwnCashierCodeCard key={`code:${selected.id}:${selected.codeVersion}`} hasCode={selected.hasCode} codeMinLength={props.codeMinLength} codeMaxLength={props.codeMaxLength}
          canManage={props.canManage && selected.canEdit} busy={props.busy} submit={(code) => props.setCode(selected, code)} />
        <OwnCashierLinksCard key={`links:${selected.id}:${selected.version}`} cashier={selected} sources={props.linkSources}
          {...(props.kinds === undefined ? {} : { kinds: props.kinds })} {...(props.sourcesLoad === undefined ? {} : { sourcesLoad: props.sourcesLoad })}
          linkResourceId={props.linkResourceId} linkSearch={props.linkSearch} candidates={props.candidates} candidatesLoading={props.candidatesLoading}
          canManage={props.canManage} busy={props.busy} chooseSource={props.chooseLinkSource} find={props.findLink} save={(links) => props.saveLinks(selected, links)}
          openSourceSettings={props.openSourceSettings} />
      </>}
    </>}
  </section>
}
